import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { listRecruiterInvites, cancelInvite, exportRecruiterInvitesCsv, apiErrorMessage, type InviteOut } from '@/api/client'
import { AppShell } from '@/components/layout/AppShell'
import { DashSkeleton, StatCard, TrajectoryChart } from '@/components/dashboard/Mosaic'
import { Progress } from '@/components/ui/progress'
import { formatDate, isExpired, formatSpan } from '@/lib/dashboard-utils'
import { buildInviteFunnel, expiryUrgency, urgencyByInvite } from '@/lib/invite-funnel'
import { usePageMeta } from '@/hooks/use-page-meta'
import { BorderBeam } from 'border-beam'
import { HoldToConfirmButton } from '@/components/spectrumui/hold-to-confirm'
import { useReducedMotion } from 'motion/react'
import { AnimatedBadge } from '@/components/motion/animated-badge'

const fmtDay = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
const fmtDayYear = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })

const CHEVRON = <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>
const ARROW_RIGHT = <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>

/**
 * Recruiter home: the same full-viewport mosaic as the candidate dashboard,
 * fed with recruiter data — usage quota in the dark tile, the featured
 * completed count, a candidate-results line, the interview index rail, the
 * awaiting-links tile with copy and cancel actions, and the invite funnel
 * strip (sent → started → completed, derived client-side in
 * lib/invite-funnel.ts).
 */
export function RecruiterDashboard() {
  usePageMeta('Your interviews — Aura Recruiter')
  const navigate = useNavigate()
  // The next-up tile's border beam is decoration — reduced motion gets the
  // same tile without the traveling light.
  const reduceMotion = useReducedMotion()
  const [data, setData] = useState<{ invites: InviteOut[]; quota_used: number; quota_limit: number } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copiedToken, setCopiedToken] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)

  const load = () => {
    setData(null)
    setError(null)
    // The funnel derives from this page, so request the endpoint's ceiling
    // (200) instead of its 50-row default — above that the funnel is still an
    // approximation, but the default truncates it for ordinary accounts.
    listRecruiterInvites(200)
      .then(setData)
      .catch(err => {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          navigate('/', { replace: true })
          return
        }
        setError('Failed to load your interviews. Please try again.')
      })
  }

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { load() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const inviteLink = (token: string) => `${window.location.origin}/invite/${token}`

  const copyLink = async (token: string) => {
    try {
      await navigator.clipboard.writeText(inviteLink(token))
      setCopiedToken(token)
      setTimeout(() => setCopiedToken(null), 2000)
    } catch {
      window.prompt('Copy this link:', inviteLink(token))
    }
  }

  const cancel = async (invite: InviteOut) => {
    // The hold-to-confirm button is the confirmation: a stray tap can't
    // complete a 1.2s hold, so no extra dialog on top. The return value
    // tells the button whether the cancel went through — on failure it
    // springs back to idle instead of flashing "Cancelled".
    try {
      await cancelInvite(invite.invite_id)
      // Update in place: load() would blank data back to null and re-run the
      // whole mosaic through its skeleton state just to drop one row.
      setData(d => d
        ? { ...d, invites: d.invites.map(i => i.invite_id === invite.invite_id ? { ...i, status: 'cancelled' as const } : i) }
        : d)
      return true
    } catch {
      setError('Could not cancel the invite.')
      return false
    }
  }

  const invites = useMemo(() => data?.invites ?? [], [data])
  const pending = useMemo(() => invites.filter(i => i.status === 'pending'), [invites])

  const exportCsv = async () => {
    // Clear the shared banner before the attempt, matching load(): a prior
    // failure must not survive a successful retry.
    setError(null)
    setExporting(true)
    try {
      await exportRecruiterInvitesCsv()
    } catch (err) {
      // The export is rate-limited 30/hour and fetchBlob types the error body
      // as a Blob, so apiErrorMessage can't read the slowapi body — special
      // case 429 rather than showing the generic "try again" for a quota.
      const status = (err as { response?: { status?: number } })?.response?.status
      setError(
        status === 429
          ? "You've reached the export limit. Please try again later."
          : apiErrorMessage(err, 'Could not export the CSV. Please try again.'),
      )
    } finally {
      setExporting(false)
    }
  }
  // A pending invite that bound a candidate was redeemed but never finished —
  // the link is spent. Expired pending links can't be copied either.
  const copyable = useMemo(
    () => pending.filter(i => !i.candidate_user_id && !isExpired(i.expires_at)),
    [pending],
  )
  const completed = invites.filter(i => i.status === 'completed')
  // The arc carries the whole verdict, not just the score — the featured
  // card's hint reads it from `latest` directly. (The previous
  // `invites.find(i => i.overall_score === latest.score)` lookup paired one
  // candidate's date with another's recommendation whenever two invites
  // shared a score.)
  const scored = [...completed]
    .filter(i => i.overall_score !== null)
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
    .map(i => ({ score: i.overall_score as number, created_at: i.created_at, recommendation: i.recommendation }))
  const latest = scored.length > 0 ? scored[scored.length - 1] : null
  const previousScore = scored.length > 1 ? scored[scored.length - 2].score : null
  const delta = previousScore !== null && latest ? latest.score - previousScore : null
  const candidatesCount = new Set(
    invites
      .filter(i => i.candidate_user_id || i.candidate_name)
      .map(i => i.candidate_user_id ?? i.candidate_name),
  ).size
  const pendingByExpiry = [...copyable].sort(
    (a, b) => new Date(a.expires_at ?? 0).getTime() - new Date(b.expires_at ?? 0).getTime(),
  )
  // Derived values are pure functions of the loaded page; memoizing keeps the
  // copy/export state changes from re-running the funnel's sorts.
  const funnel = useMemo(() => buildInviteFunnel(invites), [invites])
  // Rows inside T-24h of their expiry get the warn countdown in the awaiting
  // tile; the caption names the count so urgency survives skimming. One shared
  // clock for the caption and the rows so they agree at the T-24h boundary.
  const urgencyById = useMemo(() => urgencyByInvite(copyable), [copyable])
  const urgentAwaiting = [...urgencyById.values()].filter(u => u.level === 'urgent').length

  const loading = data === null && !error
  const empty = !loading && !error && invites.length === 0
  const openInvite = (id: string) => navigate(`/recruiter/invites/${id}`)

  return (
    <AppShell active="recruiter">
      {error && <div className="wbanner" role="alert">{error}</div>}

      <div className="dash-full">
        <header className="dash-head">
          <div>
            <h1 className="dash-head-title">Dashboard</h1>
            <p className="dash-head-sub">Create interviews with your own questions, share private links, and review reports and recordings.</p>
          </div>
          <div className="dash-head-actions">
            <button
              type="button"
              className="wbtn wbtn-secondary"
              onClick={exportCsv}
              disabled={exporting}
            >
              {exporting ? 'Exporting…' : 'Export CSV'}
            </button>
            <Link to="/recruiter/new" className="wbtn wbtn-primary">
              New interview
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
            </Link>
          </div>
        </header>

        <section className="dash-stats" aria-label="Recruiter statistics">
          <StatCard
            featured
            label="Completed"
            value={data ? completed.length : '—'}
            series={scored.map(p => p.score)}
            hint={
              latest ? (
                <span className="dash-stat-meta">
                  {latest.recommendation ?? '—'} · {fmtDay(latest.created_at)}
                  {delta !== null && delta !== 0 && <em className="dash-delta">{delta > 0 ? `▲ +${delta}` : delta}</em>}
                </span>
              ) : 'No results yet'
            }
          />
          <StatCard
            label="Awaiting"
            value={data ? pending.length : '—'}
            hint={data && pending.length > 0 ? 'waiting on candidates' : 'No pending invites'}
          />
          <StatCard
            label="Candidates"
            value={data ? candidatesCount : '—'}
            hint={data && candidatesCount > 0 ? 'invited so far' : 'Nobody invited yet'}
          />
          <StatCard
            label="All time"
            value={data ? invites.length : '—'}
            hint={data && invites.length > 0 ? 'invites created' : 'Nothing yet'}
          />
        </section>

        {loading ? (
          <DashSkeleton />
        ) : empty ? (
          <div className="dash-empty" role="status">
            <span className="dash-label">Start here</span>
            <strong>Hire with evidence, not vibes.</strong>
            <p>Create a focused interview with your own questions, share the private link, and review the report and recording when your candidate finishes.</p>
            <div className="dash-empty-steps">
              <span>1 · Create an interview with 2–5 questions</span>
              <span>2 · Share the private link with your candidate</span>
              <span>3 · Review the report and the recording</span>
            </div>
            <Link to="/recruiter/new" className="wbtn wbtn-primary">Create an interview</Link>
          </div>
        ) : (
          <>
            <section className="dash-tile dash-funnel" aria-label="Invite funnel">
              <div className="dash-tile-head">
                <h2 className="dash-tile-title">Invite funnel</h2>
                <span className="dash-caption">via private links · times UTC</span>
              </div>
              <div className="dash-funnel-flow">
                <div className="dash-funnel-stage">
                  <strong>{funnel.sent}</strong>
                  <span>sent</span>
                </div>
                <div className="dash-funnel-join">
                  <span>{funnel.startRate !== null ? `${funnel.startRate}% started` : '—'}</span>
                  {ARROW_RIGHT}
                </div>
                <div className="dash-funnel-stage">
                  <strong>{funnel.started}</strong>
                  <span>started</span>
                </div>
                <div className="dash-funnel-join">
                  <span>{funnel.completeRate !== null ? `${funnel.completeRate}% completed` : '—'}</span>
                  {ARROW_RIGHT}
                </div>
                <div className="dash-funnel-stage">
                  <strong>{funnel.completed}</strong>
                  <span>completed</span>
                </div>
                <div className="dash-funnel-medians">
                  <span>median time to start<strong>{funnel.medianTimeToStartMs !== null ? formatSpan(funnel.medianTimeToStartMs) : '—'}</strong></span>
                  <span>median time to complete<strong>{funnel.medianTimeToCompleteMs !== null ? formatSpan(funnel.medianTimeToCompleteMs) : '—'}</strong></span>
                </div>
              </div>
            </section>

            <section className="dash-tile dash-chart" aria-label="Candidate results">
              <div className="dash-tile-head">
                <h2 className="dash-tile-title">Candidate results</h2>
                <span className="dash-caption">0–100 across rubric lines</span>
              </div>
              <TrajectoryChart arc={scored} />
            </section>

            <BorderBeam size="md" colorVariant="ocean" theme="dark" active={!reduceMotion} borderRadius={16} className="dash-next">
              <span className="dash-label">Next up</span>
              <strong className="dash-next-title">Ready for the next conversation?</strong>
              <span className="dash-next-meta">Create a focused interview from a role and share a private candidate link.</span>
              <Link to="/recruiter/new" className="wbtn wbtn-primary">New interview</Link>
            </BorderBeam>

            <section className="dash-tile dash-rail-tile" aria-label="Interview index">
              <div className="dash-tile-head">
                <h2 className="dash-tile-title">Interviews</h2>
                <span className="dash-caption">{invites.length} total</span>
              </div>
              <ul className="dash-rail">
                {invites.slice(0, 8).map(inv => (
                  <li key={inv.invite_id}>
                    <button type="button" className="dash-rail-row" onClick={() => openInvite(inv.invite_id)}>
                      <span className={`dash-rail-mark${inv.overall_score !== null ? ' is-scored' : ''}`}>{inv.overall_score ?? '·'}</span>
                      <span className="dash-rail-body">
                        <strong>{inv.title}</strong>
                        <span>{inv.candidate_name ?? 'No candidate yet'} · {fmtDayYear(inv.created_at)}</span>
                      </span>
                      <span className="dash-rail-go" aria-hidden="true">{CHEVRON}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>

            <section className="dash-tile dash-tile-rows dash-rows-copy" aria-label="Awaiting candidates">
              <div className="dash-tile-head">
                <h2 className="dash-tile-title">Awaiting candidates</h2>
                <span className="dash-caption">
                  {urgentAwaiting > 0 ? `${urgentAwaiting} of ${copyable.length} expiring within 24h` : `${copyable.length} live`}
                </span>
              </div>
              {copyable.length === 0 ? (
                <div className="dash-line-empty">No live links right now — create an interview to invite a candidate.</div>
              ) : (
                <ul className="dash-rows">
                  {pendingByExpiry.slice(0, 4).map(inv => {
                    // Inside T-24h the quiet expiry date becomes a countdown
                    // and the chip flips to the warn tint, so the row reads
                    // as "chase this candidate now" at a glance.
                    const urgency = urgencyById.get(inv.invite_id) ?? expiryUrgency(inv.expires_at)
                    const urgent = urgency.level === 'urgent'
                    return (
                      <li key={inv.invite_id}>
                        <div className="dash-row">
                          <span className="dash-row-when">
                            <span className="dash-row-title">{inv.title}</span>
                            <span className={`dash-row-sub${urgent ? ' is-urgent' : ''}`}>
                              {urgency.label ?? (inv.expires_at ? `Expires ${formatDate(inv.expires_at)}` : 'No expiry')}
                            </span>
                          </span>
                          <AnimatedBadge status={urgent ? "warning" : "info"} size="sm">awaiting</AnimatedBadge>
                          <span className="dash-row-actions">
                            <button
                              type="button"
                              className="wbtn wbtn-secondary wbtn-sm"
                              onClick={() => copyLink(inv.token)}
                            >
                              <span className="wcopy-swap" key={copiedToken === inv.token ? 'copied' : 'copy'}>
                                {copiedToken === inv.token ? 'Copied!' : 'Copy link'}
                              </span>
                            </button>
                            {/* Press-and-hold replaces the old confirm() dialog:
                                the fill ring IS the confirmation, and releasing
                                early springs back without cancelling. The button
                                reads cancel()'s result — a failed request keeps
                                it idle instead of flashing "Cancelled". */}
                            <HoldToConfirmButton
                              size="sm"
                              duration={1200}
                              label="Hold to cancel"
                              confirmedLabel="Cancelled"
                              onConfirm={() => cancel(inv)}
                              className="hold-danger"
                            />
                          </span>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              )}
            </section>

            <div className="dash-dark">
              <span className="dash-label">Monthly usage</span>
              <strong className="dash-dark-digits">{data ? `${data.quota_used} / ${data.quota_limit}` : '--'}</strong>
              <span className="dash-dark-sub">interviews started this month</span>
              {data && data.quota_limit > 0 && (
                <Progress
                  value={(data.quota_used / data.quota_limit) * 100}
                  className="dash-quota-meter"
                  aria-label={`Monthly quota: ${data.quota_used} of ${data.quota_limit} interviews used`}
                />
              )}
            </div>
          </>
        )}
      </div>
    </AppShell>
  )
}
